import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { queryClient } from '../services/queryClient';
import { sessionManager } from '../services/sessionManager';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup axios defaults on mount
  useEffect(() => {
    const token = sessionManager.getToken();
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const userData = sessionManager.getUser();
      if (userData) {
        setUser(userData);
      } else {
        // Invalid stored data, clear it
        sessionManager.clearSession();
      }
    }
    setLoading(false);
  }, []);

  // Setup axios interceptor for token refresh and error handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // If 401 and we have a refresh token, try to refresh
        if (error.response?.status === 401) {
          if (sessionManager.getRefreshToken() && !error.config._retry) {
            error.config._retry = true;

            try {
              const token = await sessionManager.refresh();
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              error.config.headers['Authorization'] = `Bearer ${token}`;

              return axios(error.config);
            } catch (refreshError) {
              // Refresh failed, logout user
              logout();
              window.location.href = '/login';
            }
          } else {
            // No refresh token or retry already attempted
            logout();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Login with email and password
  const login = async (email, password) => {
    // SECURITY FIX: Clear any existing cache before login
    // This prevents seeing previous tenant's data
    queryClient.clear();
    
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/auth/login`, 
      { email, password }
    );
    
    const { token, refreshToken, user } = response.data;

    // Store tokens
    sessionManager.setTokens(token, refreshToken);
    sessionManager.setUser(user);
    
    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Update state
    setUser(user);
    
    return user;
  };

  // Forgot password - request reset email
  const forgotPassword = async (email) => {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/auth/forgot-password`,
      { email }
    );
  };

  // Reset password with token
  const resetPassword = async (token, password) => {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/auth/reset-password`,
      { token, password }
    );
  };

  // Change password (while logged in)
  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword }
    );
  };

  // Logout - SECURITY FIX: Now clears React Query cache
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate refresh token
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`);
    } catch (e) {
      // Ignore errors on logout
    }
    
    // Clear local storage
    sessionManager.clearSession();
    
    // Clear axios header
    delete axios.defaults.headers.common['Authorization'];
    
    // SECURITY FIX: Clear ALL cached data
    // This is critical to prevent data leakage between tenants
    queryClient.clear();
    
    // Clear state
    setUser(null);
  };

  // ========================================
  // LEGACY METHODS (for backward compatibility)
  // These can be removed once fully migrated
  // ========================================
  
  // Legacy OTP login - now redirects to password
  const loginWithOTP = async (email) => {
    console.warn('OTP login is deprecated. Please use password login.');
    throw new Error('Please use password login instead of OTP');
  };

  // Legacy OTP verify
  const verifyOTP = async (userId, otp) => {
    console.warn('OTP verification is deprecated. Please use password login.');
    throw new Error('Please use password login instead of OTP');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login,
      logout,
      forgotPassword,
      resetPassword,
      changePassword,
      // Legacy methods (deprecated)
      loginWithOTP,
      verifyOTP
    }}>
      {children}
    </AuthContext.Provider>
  );
};